import { useEffect, useRef, useState } from 'react';

/** Trim ends and collapse runs of internal whitespace to a single space. */
export const normalizeSearch = (s: string) => s.trim().replace(/\s+/g, ' ');

interface Options {
  /** Idle time after the last keystroke before the query term updates. */
  delay?: number;
  /** Below this length the term resolves to '' (no query fired). 0 disables. */
  minLength?: number;
}

/**
 * Search box helper that only surfaces a new query term once the user has
 * actually stopped typing, and avoids redundant API calls:
 *   - the raw `value` drives the input for a responsive UI
 *   - `debounced` is normalized (trimmed, whitespace-collapsed) so "a " and "a"
 *     never produce two different fetches
 *   - clearing the box updates instantly, skipping the debounce delay
 *   - a no-op edit (e.g. adding a trailing space) never changes `debounced`
 * `isDebouncing` is true while a change is still pending, for a subtle spinner.
 */
export function useDebouncedSearch(initial = '', { delay = 500, minLength = 0 }: Options = {}) {
  const [value, setValue] = useState(initial);
  const [debounced, setDebounced] = useState(() => normalizeSearch(initial));
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const effective = (() => {
    const n = normalizeSearch(value);
    return n.length >= minLength ? n : '';
  })();

  useEffect(() => {
    if (effective === debounced) {
      if (timer.current) clearTimeout(timer.current);
      return;
    }
    // Clearing the box should feel instant — don't wait out the delay.
    if (effective === '') {
      if (timer.current) clearTimeout(timer.current);
      setDebounced('');
      return;
    }
    timer.current = setTimeout(() => setDebounced(effective), delay);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [effective, debounced, delay]);

  return {
    value,
    setValue,
    debounced,
    isDebouncing: effective !== debounced,
    reset: () => setValue(''),
  };
}

export default useDebouncedSearch;
